import type { SyncSpec } from "./definition";

export type DestinationType =
  | "iterable"
  | "sfmc"
  | "s3"
  | "braze"
  | "klaviyo"
  | "attentive"
  | "eloqua"
  | "adobe-experience-platform"
  | "cordial"
  | "onesignal"
  | "adobe-campaign-classic"
  | "hightouch-personalization-api"
  | "mongodb"
  | "marketo"
  | "airship"
  | "moengage";

/**
 * Variable name for the preview trace ID that registered-mode previews inject
 * into `hightouch_recommendation.variables` (reaching ESPs at
 * `recommendation.variables.AidPreviewTraceId`). The published per-ESP
 * template snippets reference this exact name — do not rename without
 * updating them.
 */
export const PREVIEW_TRACE_ID_VARIABLE = "AidPreviewTraceId";

export const PARSED_RECS = `{% assign parsedRecs = row['hightouch_recommendation'] | parse %}`;
export const PARSED_USER = `{% assign parsedUser = row['hightouch_user'] | parse %}`;

export const hightouchMapping = {
  type: "template",
  template: `${PARSED_RECS}${PARSED_USER}{{ | json_construct: 'user', parsedUser, 'recommendation', parsedRecs }}`,
  to: "hightouch",
} as const;

export const getMappingForVariable = (variable: string, to: string) => ({
  type: "template" as const,
  template: `${PARSED_RECS}{{ parsedRecs['variables']['${variable}'] }}`,
  to,
});

export const getMappingForUserColumn = (column: string, to: string) => ({
  type: "template" as const,
  template: `${PARSED_USER}{{ parsedUser['${column}'] }}`,
  to,
});

/**
 * Map of message ID to its key-value tags.
 * Used to embed message tags in the Liquid template for destination payloads.
 */
export type MessageTagMap = Record<string, Record<string, string>>;

/**
 * Filters out null-valued tags from a raw tag record.
 */
export function filterNullTags(
  raw: Record<string, string | null>,
): Record<string, string> {
  const filtered: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (v != null) filtered[k] = v;
  }
  return filtered;
}

/**
 * Builds a MessageTagMap containing a single message's tags.
 * Filters out null-valued tags and returns an empty map if no tags remain.
 */
export function buildSingleMessageTagMap(
  messageId: string,
  rawTags: unknown,
): MessageTagMap {
  if (rawTags == null || typeof rawTags !== "object") {
    return {};
  }
  return buildTagMap([
    { id: messageId, tags: rawTags as Record<string, string | null> },
  ]);
}

/**
 * Builds a MessageTagMap from an array of messages with tags.
 * Filters out null-valued tags and omits messages with no remaining tags.
 */
export function buildTagMap(
  messages: Array<{ id: string; tags?: unknown }>,
): MessageTagMap {
  const tagMap: MessageTagMap = {};
  for (const msg of messages) {
    const raw = msg.tags;
    if (raw == null || typeof raw !== "object") continue;
    const filtered = filterNullTags(raw as Record<string, string | null>);
    if (Object.keys(filtered).length > 0) {
      tagMap[msg.id] = filtered;
    }
  }
  return tagMap;
}

/**
 * Returns a hightouch mapping that includes message tags in the recommendation payload.
 */
export function hightouchMappingWithTags(tagMap: MessageTagMap | undefined) {
  if (!tagMap || Object.keys(tagMap).length === 0) {
    return hightouchMapping;
  }

  return {
    type: "template" as const,
    template: `${PARSED_RECS}${PARSED_USER}{% assign parsedTagMap = tagMap | parse %}{% assign msgTags = parsedTagMap[row['message_id']] %}{% if msgTags %}{% assign parsedRecs = parsedRecs | json_construct: 'tags', msgTags %}{% endif %}{{ | json_construct: 'user', parsedUser, 'recommendation', parsedRecs }}`,
    to: "hightouch",
    scope: { tagMap: JSON.stringify(tagMap) },
  };
}

export const generateDataExtensionKey = (
  unitId: string,
  scopeId: string,
): string => {
  return `HT_AID_CHANNEL_${unitId}_${scopeId}`;
};

/**
 * The `hightouch` payload mapping. AID sends the user and the recommendation,
 * with per-message tags when present. A journey sync sends the user only.
 */
export function hightouchPayloadMapping({
  producer,
  messageTags,
}: Pick<SyncSpec, "producer" | "messageTags">) {
  if (producer === "journey") {
    return {
      type: "template" as const,
      template: `${PARSED_USER}{{ | json_construct: 'user', parsedUser }}`,
      to: "hightouch",
    };
  }
  return hightouchMappingWithTags(messageTags);
}
